/**
 * The stacked key/value block metadata cards are built from — the Integration
 * card's Campaign / Round / Contest ID, and anything shaped like it
 * (design-system.md §6 "Definition list"; `.dl-stack` in the prototype's
 * contest-overview.css).
 *
 * A real `<dl>`, not a styled table or a stack of divs. The rows are semantically
 * term/definition pairs, and the markup may as well say so — it costs nothing and
 * gives assistive tech the pairing for free.
 *
 * Values are monospace with tabular figures throughout: the column is read down,
 * and proportional digits make dates and ids of the same length look ragged.
 */
import Box from '@mui/material/Box';
import type { ReactNode } from 'react';
import type { Theme } from '@mui/material/styles';

export interface DefinitionListItem {
  key: string;
  value: ReactNode;
  /**
   * An identifier rather than a value an operator acts on. Renders a shade
   * lighter, so a card of mostly-ids doesn't read as a wall of equal weight.
   */
  id?: boolean;
}

interface DefinitionListProps {
  items: DefinitionListItem[];
}

export const DefinitionList = ({ items }: DefinitionListProps) => (
  <Box component="dl" sx={{ display: 'flex', flexDirection: 'column', margin: 0 }}>
    {items.map((item, index) => (
      <Box
        key={item.key}
        sx={(theme: Theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: `${theme.tokens.space[3]}px`,
          padding: '10px 0',
          // The rule separates rows, so the last one has nothing to separate from.
          borderBottom: index === items.length - 1 ? 'none' : theme.tokens.border.default,
        })}
      >
        <Box
          component="dt"
          sx={(theme: Theme) => ({ fontSize: '13px', color: theme.tokens.color.ink3 })}
        >
          {item.key}
        </Box>
        <Box
          component="dd"
          sx={(theme: Theme) => ({
            margin: 0,
            fontFamily: theme.tokens.font.mono,
            fontSize: '14px',
            fontWeight: 500,
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'right',
            color: item.id ? theme.tokens.color.ink3 : theme.tokens.color.ink,
          })}
        >
          {item.value}
        </Box>
      </Box>
    ))}
  </Box>
);
